/* eslint-disable no-param-reassign */
/* eslint-disable no-underscore-dangle */
import moment from 'moment';

import { DateUtil } from '@src/packages/utils/dates';
import { get } from 'packages/utils/storage';


import { getExecutionSortPriority } from '@src/models/execution';

import { getUserTimeZoneDate } from '..';
import { getCompletitionData } from '../triggers';

export const getActiveAndNextExecutions = executions => {
  const activeExecutions = [];
  const nextExecutions = [];
  const todayDate = moment();

  if (!Array.isArray(executions)) {
    return { activeExecutions, nextExecutions };
  }

  executions.forEach(execution => {
    if (!execution) return;

    const startMs = DateUtil.getTimestampMs(execution.startAt);
    const endMs = DateUtil.getTimestampMs(execution.endAt);
    if (startMs == null || endMs == null) return;

    const executionDate = moment(startMs);
    const endDate = moment(endMs);

    if (executionDate.isBefore(todayDate) && endDate.isAfter(todayDate)) {
      activeExecutions.push(execution);
    } else if (executionDate.isAfter(todayDate)) {
      nextExecutions.push(execution);
    }
  });

  return {
    activeExecutions,
    nextExecutions,
  };
};

export const compareExecutions = executions => {
  const pendings = [];
  const expires = [];
  const actives = [];

  const now = new Date();

  executions.forEach(e => {
    const isPending = now < e?.startAt?.toDate();
    const isExpired = now > e?.endAt?.toDate();

    if (isPending) {
      pendings.push(e);
      return;
    }
    if (isExpired) {
      expires.push(e);
      return;
    }
    // Else is Active
    actives.push(e);
  });

  actives.sort((a, b) => a.endAt.localeCompare(b.endAt));
  pendings.sort((a, b) => a.endAt.localeCompare(b.endAt));
  expires.sort((a, b) => a.endAt.localeCompare(b.endAt));

  return {
    active: actives,
    expired: expires,
    pending: pendings,
  };
};

/**
 * getUniqueExecutionsAccordingToExecutionUpdated
 * @param {Array} executionsParsed
 * @returns {Array}
 * @description This function receives an array of executions and returns an array of unique executions ,
 *  since when a "Work orders" is updated due to uptating members the BE process has a little delay and the same "Work orders"
 * is returned with the same id but with different startAt, so we need to keep the one with the latest startAt
 */
export const getUniqueExecutionsAccordingToExecutionUpdated = executionsParsed => {
  const elements = executionsParsed.reduce((accum, cur) => {
    // Check if schedule.id already exists in the accumulator
    const exists = Object.values(accum).some(item => item.schedule.id === cur.schedule.id);

    // If it doesn't exist, add it to the accumulator
    if (!exists) {
      accum[cur.id] = cur;
    } else {
      // If it exists, compare the startAt and keep the one with the latest startAt
      const stored = Object.values(accum).find(e => e.schedule.id === cur.schedule.id);

      delete accum[stored.id];
      const curStartMs = DateUtil.getTimestampMs(cur.startAt) ?? 0;
      const storedStartMs = DateUtil.getTimestampMs(stored.startAt) ?? 0;
      if (curStartMs > storedStartMs) {
        accum[cur.id] = cur;
      } else {
        accum[stored.id] = stored;
      }
    }

    return accum;
  }, {});

  return Object.values(elements);
};


export const executionFactory = (data = []) => {
  if (!data) return [];
  const profileTimeZone = get('timeZone') || 'America/Argentina/Buenos_Aires';
  
  const output = data?.map(record => {
    const {
      division: { id: divId },
      startAt,
      endAt,
      schedule: {
        schedule,
        expiredAllowedTime,
        isExpiredAllowed,
        triggers: pureTriggers
      },
      deliveryState = {},
      viewed = [],
      delivered = [],
      schedule: scheduleData,
      createdAt,
    } = record;

    // Timestamps may be Firestore Timestamp, `{ seconds }`, or `{ _seconds }`
    // (Elastic / serialized). Never call `.toMillis()` directly.
    const startMs = DateUtil.getTimestampMs(startAt) ?? 0;
    const endMs = DateUtil.getTimestampMs(endAt) ?? 0;
    const createdAtMs = DateUtil.getTimestampMs(createdAt) ?? 0;
    const scheduleCreatedAtMs = DateUtil.getTimestampMs(scheduleData?.createdAt) ?? 0;

    const startTz = getUserTimeZoneDate(
      startMs,
      schedule.timeZone,
      profileTimeZone,
    ).jsDate;
    const endTz = getUserTimeZoneDate(
      endMs,
      schedule.timeZone,
      profileTimeZone,
    ).jsDate;

    const scheduleCreatedAt = getUserTimeZoneDate(
      scheduleCreatedAtMs,
      schedule.timeZone,
      profileTimeZone,
    ).jsDate;

    const executionCreatedAtTz = getUserTimeZoneDate(
      createdAtMs,
      schedule.timeZone,
      profileTimeZone,
    ).jsDate;
    const executionEndAtTz = getUserTimeZoneDate(
      endMs,
      schedule.timeZone,
      profileTimeZone,
    ).jsDate;

    const hasExtraTime = isExpiredAllowed || false;
    const endTime = endTz.getTime();
    const timeLeft = Math.max(0, Math.floor((endTime - Date.now()) / (1000 * 60)));

    const endExtraTime = endTime + expiredAllowedTime * 60 * 1000;
    const endTimeWithExtra = hasExtraTime ? endExtraTime : endTime;
    const triggers = record?.triggers?.length > 0 ? record.triggers : pureTriggers;
    const triggerCompletition = getCompletitionData(triggers);
    const status = record?.state?.currentState;

    return {
      ...record,
      divId,
      startTz,
      endTz,
      hasExtraTime,
      endExtraTime,
      timeLeft,
      status,
      triggers,
      triggerCompletition,
      triggersProgress: triggerCompletition?.finishedPercentage || 0,
      endTimeWithExtra,
      endTime,
      deliveryState: {
        viewed: deliveryState?.viewed || viewed || [],
        delivered: deliveryState?.delivered || delivered || [],
      },
      scheduleCreatedAt,
      priority: getExecutionSortPriority(status),
      executionCreatedAtTz,
      executionEndAtTz,
    };
  });

  const uniqueExecutions = getUniqueExecutionsAccordingToExecutionUpdated(output);

  return uniqueExecutions;
};
